import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, ImageIcon, Film } from 'lucide-react';
import { uploadsApi, toAbs } from '../api/client';

export const ImagesUploader = ({ images = [], onChange, max = 5 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError('');
    const slots = max - images.length;
    if (slots <= 0) { setError(`Maximal ${max} Fotos erlaubt`); return; }
    setUploading(true);
    try {
      const toUpload = files.slice(0, slots);
      const uploaded = [];
      for (const f of toUpload) {
        const res = await uploadsApi.image(f);
        uploaded.push(res.url);
      }
      onChange([...images, ...uploaded]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs tracking-wider uppercase text-[#c19a8a] inline-flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Fotos ({images.length}/{max})
        </label>
        <button type="button" onClick={pick} disabled={uploading || images.length >= max}
          className="text-xs text-[#c19a8a] hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Lädt hoch...' : 'Fotos hinzufügen'}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
        multiple onChange={onFiles} className="hidden" />
      {images.length === 0 ? (
        <button type="button" onClick={pick}
          className="w-full py-8 rounded-md border border-dashed border-[#3a2530] hover:border-[#a83a5b] text-[#9a8a83] hover:text-white flex flex-col items-center gap-2 transition-colors">
          <ImageIcon className="w-8 h-8" />
          <span className="text-sm">Fotos auswählen (max {max}, je 5MB)</span>
          <span className="text-xs text-[#5a4d48]">JPG, PNG, WebP, GIF</span>
        </button>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {images.map((url, idx) => (
            <div key={idx} className="relative aspect-square rounded-md overflow-hidden bg-[#241a1f] border border-[#2a1f24] group">
              <img src={toAbs(url)} alt="" className="w-full h-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#a83a5b] text-[9px] text-white font-semibold">Haupt</span>
              )}
              <button type="button" onClick={() => remove(idx)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white hover:bg-[#a83a5b] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {images.length < max && (
            <button type="button" onClick={pick}
              className="aspect-square rounded-md border border-dashed border-[#3a2530] hover:border-[#a83a5b] text-[#9a8a83] hover:text-white flex items-center justify-center transition-colors">
              <Upload className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
      {error && <p className="text-xs text-[#ff8aa3] mt-2">{error}</p>}
    </div>
  );
};

export const VideoUploader = ({ videoUrl, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setUploading(true);
    try {
      const res = await uploadsApi.video(file);
      onChange(res.url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs tracking-wider uppercase text-[#c19a8a] inline-flex items-center gap-1">
          <Film className="w-3 h-3" /> Video (optional)
        </label>
        {videoUrl && (
          <button type="button" onClick={() => onChange(null)}
            className="text-xs text-[#ff8aa3] hover:text-white">Entfernen</button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime"
        onChange={onFile} className="hidden" />
      {videoUrl ? (
        <video src={toAbs(videoUrl)} controls
          className="w-full max-h-56 rounded-md bg-[#241a1f] border border-[#2a1f24]" />
      ) : (
        <button type="button" onClick={pick} disabled={uploading}
          className="w-full py-6 rounded-md border border-dashed border-[#3a2530] hover:border-[#a83a5b] text-[#9a8a83] hover:text-white flex flex-col items-center gap-2 transition-colors disabled:opacity-50">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Film className="w-6 h-6" />}
          <span className="text-sm">{uploading ? 'Video wird hochgeladen...' : 'Video auswählen (max 30MB)'}</span>
          <span className="text-xs text-[#5a4d48]">MP4, WebM, MOV</span>
        </button>
      )}
      {error && <p className="text-xs text-[#ff8aa3] mt-2">{error}</p>}
    </div>
  );
};
